import React from 'react';
import { User, Bot, Zap, GripVertical, ChevronDown, ChevronRight, Link2, Unlink } from 'lucide-react';
import type { Employee } from '../types/api';

// ============================================================================
// Three-state node types for the augmented org chart
// ============================================================================

export type NodeState = 'HUMAN' | 'AUGMENTED' | 'AUTONOMOUS';

export function getNodeState(employee: Employee): NodeState {
    if (!employee.replicaLink) return 'HUMAN';
    if (employee.replicaLink.linkType === 'AUTONOMOUS') return 'AUTONOMOUS';
    return 'AUGMENTED';
}

interface OrgNodeCardProps {
    employee: Employee;
    isSelected?: boolean;
    isExpanded?: boolean;
    hasChildren?: boolean;
    depth?: number;
    onSelect?: (employee: Employee) => void;
    onToggleExpand?: () => void;
    onDragStart?: (e: React.DragEvent, employee: Employee) => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDrop?: (e: React.DragEvent, targetEmployee: Employee) => void;
    compact?: boolean;
}

const NODE_STYLES: Record<NodeState, {
    border: string;
    bg: string;
    glow: string;
    badge: string;
    badgeText: string;
    icon: React.ReactNode;
}> = {
    HUMAN: {
        border: 'border-blue-500/30',
        bg: 'bg-slate-800',
        glow: '',
        badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
        badgeText: 'Human',
        icon: <User size={14} className="text-blue-400" />,
    },
    AUGMENTED: {
        border: 'border-cyan-400/40',
        bg: 'bg-gradient-to-br from-slate-800 to-slate-800/80',
        glow: 'shadow-[0_0_15px_-3px_rgba(0,229,255,0.15)]',
        badge: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
        badgeText: 'AI Augmented',
        icon: <Zap size={14} className="text-cyan-400" />,
    },
    AUTONOMOUS: {
        border: 'border-purple-500/40',
        bg: 'bg-gradient-to-br from-slate-800 to-purple-900/20',
        glow: 'shadow-[0_0_20px_-3px_rgba(168,85,247,0.2)]',
        badge: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
        badgeText: 'Autonomous AI',
        icon: <Bot size={14} className="text-purple-400" />,
    },
};

const AVATAR_STYLES: Record<NodeState, string> = {
    HUMAN: 'bg-slate-600',
    AUGMENTED: 'bg-gradient-to-br from-cyan-500 to-blue-600',
    AUTONOMOUS: 'bg-gradient-to-br from-purple-500 to-indigo-600',
};

export default function OrgNodeCard({
    employee,
    isSelected = false,
    isExpanded = false,
    hasChildren = false,
    depth = 0,
    onSelect,
    onToggleExpand,
    onDragStart,
    onDragOver,
    onDrop,
    compact = false,
}: OrgNodeCardProps) {
    const state = getNodeState(employee);
    const style = NODE_STYLES[state];

    return (
        <div
            className={`
                relative rounded-xl border ${style.border} ${style.bg} ${style.glow}
                transition-all duration-200 cursor-pointer select-none
                hover:border-opacity-70 hover:scale-[1.02]
                ${isSelected ? 'ring-2 ring-cyan-400/50 border-cyan-400/60' : ''}
                ${compact ? 'p-2.5' : 'p-3.5'}
            `}
            onClick={() => onSelect?.(employee)}
            draggable={!!onDragStart}
            onDragStart={(e) => onDragStart?.(e, employee)}
            onDragOver={(e) => { e.preventDefault(); onDragOver?.(e); }}
            onDrop={(e) => onDrop?.(e, employee)}
        >
            {/* Drag handle */}
            {onDragStart && (
                <div className="absolute top-2 right-2 text-slate-600 hover:text-slate-400 cursor-grab active:cursor-grabbing">
                    <GripVertical size={14} />
                </div>
            )}

            <div className="flex items-center gap-3">
                {/* Expand/collapse toggle */}
                {hasChildren && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
                        className="p-0.5 text-slate-400 hover:text-white transition-colors"
                    >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                )}

                {/* Avatar with state indicator */}
                <div className="relative flex-shrink-0">
                    <div className={`
                        ${compact ? 'w-9 h-9 text-xs' : 'w-11 h-11 text-sm'}
                        rounded-xl ${AVATAR_STYLES[state]} flex items-center justify-center text-white font-bold
                    `}>
                        {state === 'AUTONOMOUS' ? (
                            <Bot size={compact ? 16 : 20} />
                        ) : (
                            employee.name.charAt(0).toUpperCase()
                        )}
                    </div>

                    {/* Augmented overlay — small AI indicator on avatar */}
                    {state === 'AUGMENTED' && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-slate-900 border-2 border-cyan-400/60 flex items-center justify-center">
                            <Bot size={10} className="text-cyan-400" />
                        </div>
                    )}

                    {/* Autonomous pulse ring */}
                    {state === 'AUTONOMOUS' && (
                        <div className="absolute inset-0 rounded-xl animate-ping bg-purple-500/10" style={{ animationDuration: '3s' }} />
                    )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                    <p className={`font-semibold text-white truncate ${compact ? 'text-xs' : 'text-sm'}`}>
                        {employee.name}
                    </p>
                    {employee.title && (
                        <p className={`text-slate-400 truncate ${compact ? 'text-[10px]' : 'text-xs'}`}>
                            {employee.title}
                        </p>
                    )}
                    {!compact && employee.department && (
                        <p className="text-[10px] text-slate-500 truncate mt-0.5">
                            {employee.department}
                        </p>
                    )}
                </div>

                {/* State badge */}
                <div className={`
                    flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider flex-shrink-0
                    ${style.badge}
                `}>
                    {style.icon}
                    {!compact && <span>{style.badgeText}</span>}
                </div>
            </div>

            {/* Replica link indicator for augmented state */}
            {state === 'AUGMENTED' && !compact && (
                <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center gap-2">
                    <Link2 size={11} className="text-cyan-500/60" />
                    <span className="text-[10px] text-cyan-500/60">
                        Paired with AI Replica
                    </span>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// Mini node for the dashboard preview
// ============================================================================

export function OrgNodeMini({ employee }: { employee: Employee }) {
    const state = getNodeState(employee);
    const avatarStyle = AVATAR_STYLES[state];

    return (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700/30">
            <div className={`w-6 h-6 rounded-lg ${avatarStyle} flex items-center justify-center text-white text-[10px] font-bold`}>
                {state === 'AUTONOMOUS' ? <Bot size={12} /> : employee.name.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-white truncate">{employee.name}</p>
                <p className="text-[9px] text-slate-500 truncate">{employee.title || 'Employee'}</p>
            </div>
            {state !== 'HUMAN' && (
                <div className={`w-1.5 h-1.5 rounded-full ${state === 'AUGMENTED' ? 'bg-cyan-400' : 'bg-purple-400'}`} />
            )}
        </div>
    );
}
